import type { Devis, LigneDevis } from "@shared/schema";
import {
  calcLoyerAuto,
  FINANCEMENT_COEFFICIENTS,
  hasFinancementCoefficient,
  resolveDurees,
  resolveLoyer,
  resolveMaintenance,
} from "@/lib/financement";

function fmt(n: number) {
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, character => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[character] ?? character));
}

function normalizeMaintenanceQuantity(value: unknown): number {
  const quantity = Number(value);
  return Number.isInteger(quantity) && quantity >= 1 ? quantity : 1;
}

function multilineTextHtml(value: string | null | undefined, style: string) {
  if (value === undefined || value === null || value === "") return "";
  return `<div style="${style};white-space:pre-wrap;overflow-wrap:anywhere;">${escapeHtml(value)}</div>`;
}

function calcLigne(l: LigneDevis) {
  const pu = Number(l.puHt) || 0;
  const qty = Number(l.quantite) || 1;
  const rem = Number(l.remise) || 0;
  return pu * qty * (1 - rem / 100);
}

function calcLigneEff(l: LigneDevis): number {
  // Comptant maintenance : nouveau modèle (modePaiement==="comptant" + dureeCouverture)
  // ou ancien modèle (dureeMaintenance==="comptant") — les deux tolérés pour rétrocompatibilité
  const isComptantMaint =
    (l.modePaiement === "comptant" && l.dureeCouverture !== undefined) ||
    (!l.modePaiement && l.dureeMaintenance === "comptant");
  if (isComptantMaint) {
    const couv = parseInt(l.dureeCouverture || "48");
    const montant = l.montantComptant != null ? Number(l.montantComptant) : (Number(l.puHt) || 0) * couv;
    return montant * (Number(l.quantite) || 1) * (1 - (Number(l.remise) || 0) / 100);
  }
  return calcLigne(l);
}

/** Retourne true si la ligne est récurrente (maintenance mensuelle).
 *  - Nouveau modèle : natureLigne === 'recurrent'
 *  - Fallback anciens devis : dureeCouverture défini (maintenance avec durée de couverture)
 */
function isLigneRecurrente(l: LigneDevis): boolean {
  return l.natureLigne === "recurrent" || (l.dureeCouverture != null && l.dureeCouverture !== "");
}

export function printDevis(d: Devis, lignes: LigneDevis[], commercialName: string, logoUrl?: string, auteurName?: string | null) {
  const produits = lignes.filter(l => l.section === "produits");
  const prestations = lignes.filter(l => l.section === "prestations");

  const sousTotal = lignes.filter(l => !isLigneRecurrente(l)).reduce((acc, l) => acc + calcLigneEff(l), 0);
  const remGlobal = Number(d.remiseGlobale) || 0;
  const totalHT = sousTotal * (1 - remGlobal / 100);
  const tva = totalHT * 0.2;
  const ttc = totalHT + tva;
  const baseCapitalFinance = lignes
    .filter(l => (l.natureLigne ?? "capital") === "capital" && l.modePaiement === "finance")
    .reduce((acc, l) => acc + calcLigneEff(l), 0) * (1 - remGlobal / 100);
  const maintenanceAuto = lignes
    .filter(l => l.natureLigne === "recurrent" && l.modePaiement === "finance")
    .reduce((acc, l) => acc + calcLigneEff(l), 0);
  const lignesComptant = lignes.filter(
    l => l.modePaiement === "comptant" && (l.natureLigne ?? "capital") === "capital"
  );
  const validite = d.validiteJours || 30;

  const dateStr = d.createdAt
    ? new Date(d.createdAt).toLocaleDateString("fr-FR")
    : new Date().toLocaleDateString("fr-FR");
  const expiry = new Date(d.createdAt || Date.now());
  expiry.setDate(expiry.getDate() + validite);
  const expiryStr = expiry.toLocaleDateString("fr-FR");

  function rowProduit(l: LigneDevis) {
    const total = calcLigneEff(l);
    const isMaint = l.dureeMaintenance !== undefined;
    const isFinanced =
      l.natureLigne === "recurrent" &&
      (l.modePaiement === "finance" ||
       (!l.modePaiement && l.dureeMaintenance !== undefined && l.dureeMaintenance !== "comptant"));
    const isCapitalFinance =
      l.modePaiement === "finance" && (l.natureLigne ?? "capital") === "capital" && l.dureeFinancement;
    const hasRemise = Number(l.remise) > 0;
    const quantity = isMaint ? normalizeMaintenanceQuantity(l.quantite) : l.quantite;
    const quantitySuffix = isMaint && quantity > 1 ? ` × ${quantity}` : "";
    const precision = l.precision;
    const precisionHtml = multilineTextHtml(precision, "font-size:11px;color:#6b7280;margin-top:2px;");
    const descriptionHtml = multilineTextHtml(l.descriptionFr, "font-size:11px;color:#6b7280;margin-top:2px;");
    const bulletsHtml = Array.isArray(l.bullets) && l.bullets.length > 0
      ? `<ul style="margin:4px 0 0 12px;padding:0;list-style:disc;">${l.bullets.map(b => `<li style="font-size:10px;color:#4b5563;margin-bottom:2px;">${b}</li>`).join("")}</ul>`
      : "";

    let priceCell: string;
    if (isFinanced) {
      priceCell = `<div style="font-weight:600;font-size:13px;">${fmt(Number(l.puHt))} €/mois${quantitySuffix}</div>`;
      if (hasRemise) {
        priceCell = `<div style="text-decoration:line-through;color:#9ca3af;font-size:12px;">${fmt(Number(l.puHt))} €/mois${quantitySuffix}</div>
               <div style="font-weight:600;font-size:13px;">${fmt(Number(l.puHt) * (1 - Number(l.remise) / 100))} €/mois${quantitySuffix}</div>
               <div style="font-size:10px;color:#ef4444;">-${l.remise}%</div>`;
      }
    } else if (hasRemise) {
      priceCell = `<div style="text-decoration:line-through;color:#9ca3af;font-size:12px;">${fmt(Number(l.puHt))} €</div>
               <div style="font-weight:600;font-size:13px;">${fmt(Number(l.puHt) * (1 - Number(l.remise) / 100))} €</div>
               <div style="font-size:10px;color:#ef4444;">-${l.remise}%</div>`;
    } else {
      priceCell = `<div style="font-weight:600;">${fmt(Number(l.puHt))} €</div>`;
    }

    let totalDisplay: string;
    if (isFinanced) {
      totalDisplay = `${fmt(total)} €/mois`;
    } else if (isCapitalFinance && l.dureeFinancement) {
      const mensualite = total * (FINANCEMENT_COEFFICIENTS[l.dureeFinancement] ?? 0) / 100;
      totalDisplay = `${fmt(total)} €<div style="font-size:10px;color:#2563eb;font-weight:600;">~${fmt(mensualite)} €/mois*</div>`;
    } else {
      totalDisplay = `${fmt(total)} €`;
    }

    return `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;">
          ${l.imageUrl
            ? `<img src="${l.imageUrl}" style="width:48px;height:48px;object-fit:cover;border-radius:6px;border:1px solid #e5e7eb;" />`
            : isMaint
              ? `<div style="width:48px;height:48px;background:#eff6ff;border-radius:6px;border:1px solid #bfdbfe;display:flex;align-items:center;justify-content:center;font-size:20px;">🛡️</div>`
              : `<div style="width:48px;height:48px;background:#f3f4f6;border-radius:6px;border:1px solid #e5e7eb;"></div>`}
        </td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;">
          ${l.sku ? `<div style="font-size:11px;color:#6b7280;font-family:monospace;">${l.sku}</div>` : ""}
          <div style="font-weight:600;font-size:13px;">${l.nomFr}</div>
          ${precisionHtml}
          ${descriptionHtml}
          ${bulletsHtml}
        </td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right;white-space:nowrap;">
          ${priceCell}
        </td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center;">${quantity}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:700;">${totalDisplay}</td>
      </tr>
    `;
  }

  function tableSection(title: string, rows: LigneDevis[]) {
    if (rows.length === 0) return "";
    const hasCapitalFinance = rows.some(
      l => l.modePaiement === "finance" && (l.natureLigne ?? "capital") === "capital" && l.dureeFinancement
    );
    const noteHtml = hasCapitalFinance
      ? `<div style="padding:6px 12px;font-size:9px;color:#64748b;font-style:italic;border-top:1px solid #e5e7eb;">
           *Montant mensuel indicatif. Le loyer contractuel figure dans le tableau de financement.
         </div>`
      : "";
    return `
      <div style="margin-bottom:24px;">
        <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;">
          <div style="background:#1e293b;color:white;padding:10px 16px;font-size:12px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;">${title}</div>
          <table style="width:100%;border-collapse:collapse;">
            <thead>
              <tr style="background:#f1f5f9;">
                <th style="padding:8px;text-align:left;font-size:11px;color:#64748b;font-weight:600;width:60px;">Photo</th>
                <th style="padding:8px;text-align:left;font-size:11px;color:#64748b;font-weight:600;">Description</th>
                <th style="padding:8px;text-align:right;font-size:11px;color:#64748b;font-weight:600;white-space:nowrap;">PU HT</th>
                <th style="padding:8px;text-align:center;font-size:11px;color:#64748b;font-weight:600;">Qté</th>
                <th style="padding:8px;text-align:right;font-size:11px;color:#64748b;font-weight:600;white-space:nowrap;">Total HT</th>
              </tr>
            </thead>
            <tbody>${rows.map(rowProduit).join("")}</tbody>
          </table>
          ${noteHtml}
        </div>
      </div>
    `;
  }

  function financementTable() {
    const hasFinanceLines = lignes.some(l => l.modePaiement === "finance");
    if (!d.optionFinancement || !hasFinanceLines) return "";

    const dureesFinancement = resolveDurees(d);
    const financementColumns = dureesFinancement.map(duree => {
      const loyerAuto = hasFinancementCoefficient(duree)
        ? calcLoyerAuto(baseCapitalFinance, duree)
        : null;
      const loyer = resolveLoyer(d, duree, loyerAuto);
      const maintenance = resolveMaintenance(d, duree, maintenanceAuto);
      const totalHT = loyer === null ? null : loyer + maintenance;
      return {
        duree,
        loyer,
        maintenance,
        totalHT,
        tva: totalHT === null ? null : totalHT * 0.2,
        totalTTC: totalHT === null ? null : totalHT * 1.2,
      };
    });
    const columnCount = financementColumns.length;
    const cellPadding = columnCount === 1 ? "8px 14px" : columnCount === 2 ? "7px 10px" : "6px 6px";
    const labelFontSize = columnCount === 3 ? "10px" : "11px";
    const valueFontSize = columnCount === 3 ? "10px" : "11px";
    const totalFontSize = columnCount === 3 ? "11px" : "12px";
    const formatAmount = (value: number | null) => value === null ? "—" : `${fmt(value)} €`;

    const comptantBlock = lignesComptant.length > 0
      ? `<div style="padding:10px 14px;background:#f8fafc;border-bottom:1px solid #e2e8f0;">
          <div style="font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px;">Paiement à livraison</div>
          ${lignesComptant.map(l => `
            <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:2px;">
              <span style="color:#64748b;">${l.nomFr?.trim() || l.designation?.trim() || "Article"}</span>
              <span style="font-weight:600;">${fmt(calcLigneEff(l))} € HT</span>
            </div>
          `).join("")}
        </div>`
      : "";

    return `
      <div style="margin-bottom:24px;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;">
        <div style="background:#1e293b;color:#fff;padding:10px 14px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;">
          OPTION FINANCEMENT
        </div>
        ${comptantBlock}
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="background:#f1f5f9;">
              <th style="padding:${cellPadding};text-align:left;font-size:${labelFontSize};color:#64748b;font-weight:600;white-space:nowrap;">DURÉE</th>
              ${financementColumns.map(column => `
                <th style="padding:${cellPadding};text-align:right;font-size:${valueFontSize};font-weight:700;color:#1e293b;white-space:nowrap;">${column.duree} MOIS</th>
              `).join("")}
            </tr>
          </thead>
          <tbody>
            <tr style="border-top:1px solid #e2e8f0;">
              <td style="padding:${cellPadding};font-size:${labelFontSize};color:#475569;">Financement robot et accessoires</td>
              ${financementColumns.map(column => `
                <td style="padding:${cellPadding};text-align:right;font-size:${valueFontSize};font-weight:600;white-space:nowrap;">${formatAmount(column.loyer)}</td>
              `).join("")}
            </tr>
            <tr style="border-top:1px solid #e2e8f0;">
              <td style="padding:${cellPadding};font-size:${labelFontSize};color:#475569;">Maintenance Mensuelle</td>
              ${financementColumns.map(column => `
                <td style="padding:${cellPadding};text-align:right;font-size:${valueFontSize};font-weight:600;white-space:nowrap;">${formatAmount(column.maintenance)}</td>
              `).join("")}
            </tr>
            <tr style="border-top:1px solid #e2e8f0;background:#1e293b;color:#fff;font-weight:800;">
              <td style="padding:${cellPadding};font-size:${totalFontSize};">TOTAL MENSUEL HT</td>
              ${financementColumns.map(column => `
                <td style="padding:${cellPadding};text-align:right;font-size:${totalFontSize};white-space:nowrap;">${formatAmount(column.totalHT)}</td>
              `).join("")}
            </tr>
            <tr style="border-top:1px solid #e2e8f0;">
              <td style="padding:${cellPadding};font-size:${labelFontSize};color:#64748b;">TVA (20 %)</td>
              ${financementColumns.map(column => `
                <td style="padding:${cellPadding};text-align:right;font-size:${valueFontSize};color:#64748b;white-space:nowrap;">${formatAmount(column.tva)}</td>
              `).join("")}
            </tr>
            <tr style="border-top:1px solid #e2e8f0;">
              <td style="padding:${cellPadding};font-size:${labelFontSize};color:#64748b;">TOTAL MENSUEL TTC</td>
              ${financementColumns.map(column => `
                <td style="padding:${cellPadding};text-align:right;font-size:${valueFontSize};color:#64748b;white-space:nowrap;">${formatAmount(column.totalTTC)}</td>
              `).join("")}
            </tr>
          </tbody>
        </table>
      </div>
    `;
  }

  const logoHtml = logoUrl
    ? `<img src="${logoUrl}" alt="Logo" style="height:48px;max-width:160px;object-fit:contain;" />`
    : `<div style="font-size:28px;font-weight:800;color:#1e293b;letter-spacing:-0.5px;">JOBOTECH</div>`;

  const ribBlock = `
    <div style="margin-top:32px;padding-top:20px;border-top:1px solid #e2e8f0;">
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px;margin-bottom:12px;">
        <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;margin-bottom:8px;">RIB</div>
        <div style="font-size:11px;color:#374151;margin-bottom:3px;"><strong>Établissement :</strong> QONTO</div>
        <div style="font-size:11px;color:#374151;margin-bottom:3px;"><strong>IBAN :</strong> FR76 1695 8000 0182 7628 3540 174</div>
        <div style="font-size:11px;color:#374151;"><strong>BIC :</strong> QNTOFRP1XXX</div>
      </div>
      <div style="font-size:9px;color:#6b7280;line-height:1.6;">
        Merci d'inclure la référence du devis dans le libellé de votre virement. Pénalités de retard : trois fois le taux annuel d'intérêt légal en vigueur depuis la date d'échéance jusqu'à complet paiement. Indemnité forfaitaire pour frais de recouvrement en cas de retard de paiement : 40 €. Devis gratuit. Validité 1 mois. Délais de livraison : selon stock disponible, sinon 8 à 10 semaines à réception du règlement.
      </div>
    </div>
  `;

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>Devis ${d.numero} - ${d.societe || d.contactNom || ""}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1e293b; background: white; font-size: 13px; }
    .page { max-width: 800px; margin: 0 auto; padding: 40px; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; margin: 0; }
      .no-print { display: none; }
      @page { margin: 0; }
      .page {
        padding: 15mm;
        max-width: 100%;
        -webkit-box-decoration-break: clone;
        box-decoration-break: clone;
      }
      .page tr { break-inside: avoid; page-break-inside: avoid; }
    }
    h1 { font-size: 28px; font-weight: 800; }
    h2 { font-size: 18px; font-weight: 700; }
    h3 { font-size: 13px; font-weight: 700; }
  </style>
</head>
<body>
  <div class="page">
    <!-- Print button -->
    <div class="no-print" style="margin-bottom:20px;text-align:right;">
      <button onclick="window.print()" style="background:#1e293b;color:white;border:none;padding:10px 20px;border-radius:8px;font-size:14px;cursor:pointer;font-weight:600;">⬇ Enregistrer / Imprimer</button>
    </div>

    <!-- Header -->
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px;padding-bottom:24px;border-bottom:2px solid #e2e8f0;">
      <div>
        ${logoHtml}
        <div style="color:#64748b;font-size:12px;margin-top:4px;">contact@jobotto.fr</div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:24px;font-weight:700;color:#2563eb;">DEVIS</div>
        <div style="font-size:16px;font-weight:600;margin-top:4px;">N° ${d.numero}</div>
        <div style="color:#64748b;font-size:12px;margin-top:4px;">Date : ${dateStr}</div>
        <div style="color:#64748b;font-size:12px;">Validité : ${validite} jours (jusqu'au ${expiryStr})</div>
        ${commercialName !== "—" ? `<div style="color:#64748b;font-size:12px;margin-top:4px;">Commercial : ${commercialName}</div>` : ""}
        ${auteurName ? `<div style="color:#64748b;font-size:12px;margin-top:4px;">Auteur : ${auteurName}</div>` : ""}
      </div>
    </div>

    <!-- Destinataire + Objet -->
    <div style="display:flex;gap:24px;margin-bottom:32px;">
      <div style="flex:1;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:16px;">
        <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;margin-bottom:8px;">Destinataire</div>
        ${d.societe ? `<div style="font-weight:700;font-size:15px;">${d.societe}</div>` : ""}
        ${d.contactNom ? `<div style="color:#374151;margin-top:2px;">${d.contactCivilite ? d.contactCivilite + " " : ""}${d.contactNom}</div>` : ""}
        ${d.adresse ? `<div style="color:#6b7280;font-size:12px;margin-top:6px;line-height:1.5;">${d.adresse.replace(/\n/g, "<br>")}</div>` : ""}
      </div>
      ${d.objet ? `
        <div style="flex:1;background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:16px;">
          <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#3b82f6;margin-bottom:8px;">Objet de la demande</div>
          <div style="font-weight:600;color:#1e40af;">${d.objet}</div>
        </div>
      ` : ""}
    </div>

    <!-- Lignes produits -->
    ${tableSection("Produits & équipements", produits)}
    ${tableSection("Prestations à la commande", prestations)}

    <!-- Récapitulatif -->
    <div style="display:flex;justify-content:flex-end;margin-bottom:24px;">
      <div style="min-width:320px;">
        <table style="width:100%;border-collapse:collapse;">
          <tr>
            <td style="padding:6px 0;color:#64748b;">Sous-total HT</td>
            <td style="padding:6px 0;text-align:right;font-weight:600;">${fmt(sousTotal)} €</td>
          </tr>
          ${remGlobal > 0 ? `
          <tr>
            <td style="padding:6px 0;color:#ef4444;">Remise globale (-${remGlobal}%)</td>
            <td style="padding:6px 0;text-align:right;color:#ef4444;font-weight:600;">-${fmt(sousTotal - totalHT)} €</td>
          </tr>` : ""}
          <tr style="background:#1e293b;color:white;border-radius:8px;">
            <td style="padding:12px 16px;font-weight:700;font-size:16px;border-radius:8px 0 0 8px;">Total HT</td>
            <td style="padding:12px 16px;text-align:right;font-weight:800;font-size:18px;border-radius:0 8px 8px 0;">${fmt(totalHT)} €</td>
          </tr>
          <tr>
            <td style="padding:6px 0;font-size:12px;color:#64748b;">TVA 20%</td>
            <td style="padding:6px 0;font-size:12px;text-align:right;color:#64748b;">${fmt(tva)} €</td>
          </tr>
          <tr>
            <td style="padding:6px 0;font-size:12px;color:#64748b;">Net TTC</td>
            <td style="padding:6px 0;font-size:12px;text-align:right;color:#64748b;">${fmt(ttc)} €</td>
          </tr>
        </table>
      </div>
    </div>

    <!-- Zone signature -->
    <div style="display:flex;gap:24px;margin-bottom:32px;">
      <div style="flex:1;border:1px solid #e2e8f0;border-radius:8px;padding:16px;min-height:80px;">
        <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;margin-bottom:8px;">Bon pour accord — Signature client</div>
        <div style="font-size:11px;color:#94a3b8;">Date : _______________</div>
      </div>
      <div style="flex:1;border:1px solid #e2e8f0;border-radius:8px;padding:16px;min-height:80px;">
        <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;margin-bottom:8px;">Cachet et signature</div>
      </div>
    </div>

    <!-- Modalités de paiement -->
    ${d.modalitesPaiement && d.modalitesPaiement.filter(m => !m.includes("mois")).length > 0 ? `
    <div style="margin-bottom:16px;">
      <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;margin-bottom:4px;">Modalités de paiement</div>
      <div>${d.modalitesPaiement.filter(m => !m.includes("mois")).join(" · ")}</div>
    </div>` : ""}

    <!-- Financement -->
    ${financementTable()}

    <!-- Notes -->
    ${d.notes ? `
    <div style="background:#fefce8;border:1px solid #fef08a;border-radius:8px;padding:16px;margin-bottom:24px;">
      <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#a16207;margin-bottom:6px;">Notes / Conditions particulières</div>
      <div style="font-size:12px;color:#713f12;line-height:1.6;">${d.notes.replace(/\n/g, "<br>")}</div>
    </div>` : ""}

    <!-- RIB + Informations complémentaires -->
    ${ribBlock}
  </div>
</body>
</html>`;

  const win = window.open("", "_blank");
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}
